import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { collateralApi, ApiError } from '../../../services/collateralApi';

const TAB_KEYS = ['general', 'metrics', 'receivables'];

export default function useGuaranteePageView() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<any>(null);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [editingIds, setEditingIds] = useState<string[]>([]);
    const [showValidationPanel, setShowValidationPanel] = useState(false);
    const [batchProcess, setBatchProcess] = useState<Record<string, { received: string; status: string; observation: string }>>({});
    const [batchStatus, setBatchStatus] = useState('');
    const [batchObservation, setBatchObservation] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [sortField, setSortField] = useState('titleNumber');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

    const tabIndex = Math.max(0, TAB_KEYS.indexOf(searchParams.get('tab') || 'general'));
    const setTabIndex = (idx: number) => setSearchParams({ tab: TAB_KEYS[idx] });

    useEffect(() => {
        if (!id) return;
        setIsLoading(true);
        collateralApi.getGuaranteeView(id)
            .then(setData)
            .catch((e: any) => {
                if (e instanceof ApiError) {
                    if (e.status === 403) return navigate('/denied');
                    if (e.status === 404) return navigate('/not-found');
                }
                navigate('/error');
            })
            .finally(() => setIsLoading(false));
    }, [id]);

    const titles = useMemo(() => {
        const list = data?.titles || [];
        return [...list].sort((a: any, b: any) => {
            const va = a[sortField] ?? '';
            const vb = b[sortField] ?? '';
            const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb), undefined, { numeric: true });
            return sortOrder === 'ASC' ? cmp : -cmp;
        });
    }, [data, sortField, sortOrder]);

    const enabledProcesses = useMemo(() => {
        if (!data?.validationConfig) return [];
        const config = data.validationConfig;
        const isEnabled = (v: unknown) => v && v !== 'DO_NOT_VALIDATE';
        const processes: string[] = [];
        if (isEnabled(config.validation_xml)) processes.push('XML');
        if (isEnabled(config.validation_contact)) processes.push('DRAWEE');
        if (isEnabled(config.validation_invoice_pdf)) processes.push('INVOICE_PDF');
        if (isEnabled(config.validation_purchase_contract)) processes.push('PURCHASE_CONTRACT');
        if (isEnabled(config.validation_receipt_stub)) processes.push('RECEIPT_STUB');
        if (isEnabled(config.validation_sefaz)) processes.push('SEFAZ');
        if (isEnabled(config.validation_accepted_bill)) processes.push('ACCEPTED_BILL');
        return processes;
    }, [data]);

    const summary = useMemo(() => {
        if (!data) return { totalAmount: 0, validatedAmount: 0, docsCompleteAmount: 0, docsIncompleteAmount: 0 };
        const totalAmount = data.totalAmount || 0;
        const validatedAmount = totalAmount * ((data.validatedPercentage || 0) / 100);
        const docsCompleteAmount = titles.filter((t: any) => t.docStatus === 'OK').reduce((s: number, t: any) => s + t.amount, 0);
        const docsIncompleteAmount = totalAmount - docsCompleteAmount;
        return { totalAmount, validatedAmount, docsCompleteAmount, docsIncompleteAmount };
    }, [data, titles]);

    const prefillAndOpenPanel = (ids: string[]) => {
        const selected = titles.filter((t: any) => ids.includes(t.id));
        const prefilled: Record<string, { received: string; status: string; observation: string }> = {};
        for (const p of enabledProcesses) {
            if (p === 'DRAWEE') continue;
            const items = selected.map((t: any) => (t.processItems || []).find((pi: any) => pi.processType === p)).filter(Boolean);
            const allReceived = items.length > 0 && items.every((i: any) => i.received === true);
            const allNotReceived = items.length > 0 && items.every((i: any) => i.received === false);
            const statuses = items.map((i: any) => i.status).filter(Boolean);
            const allSameStatus = statuses.length > 0 && statuses.every((s: string) => s === statuses[0]);
            const observations = items.map((i: any) => i.observation).filter(Boolean);
            const allSameObs = observations.length > 0 && observations.every((o: string) => o === observations[0]);
            prefilled[p] = {
                received: allReceived ? 'true' : allNotReceived ? 'false' : '',
                status: allSameStatus ? statuses[0] : '',
                observation: allSameObs ? observations[0] : '',
            };
        }
        setBatchProcess(prefilled);
        const docStatuses = selected.map((t: any) => t.docStatus).filter(Boolean);
        const allSameDoc = docStatuses.length > 0 && docStatuses.every((s: string) => s === docStatuses[0]);
        const DOC_STATUS_REVERSE: Record<string, string> = { 'Pendente': 'PENDING', 'OK': 'OK', 'Não OK': 'NOT_OK' };
        setBatchStatus(allSameDoc ? (DOC_STATUS_REVERSE[docStatuses[0]] || docStatuses[0]) : '');
        const obs = selected.map((t: any) => t.batchObservation).filter(Boolean);
        const allSameObs = obs.length > 0 && obs.every((o: string) => o === obs[0]);
        setBatchObservation(allSameObs ? obs[0] : '');
        setShowValidationPanel(true);
    };

    const openBatchPanel = () => {
        setEditingIds([]);
        prefillAndOpenPanel(Array.from(selectedIds));
    };

    const openEditPanel = (itemId: string) => {
        setEditingIds([itemId]);
        prefillAndOpenPanel([itemId]);
    };

    const toggleSelect = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleSaveValidation = async () => {
        if (!id) return;
        setIsSaving(true);
        try {
            const ids = editingIds.length > 0 ? editingIds : Array.from(selectedIds);
            const validations = Object.entries(batchProcess)
                .filter(([, { received }]) => received !== '')
                .map(([processType, { received, status, observation }]) => ({
                    processType,
                    received: received === 'true',
                    status: status || batchStatus || undefined,
                    observation: observation || batchObservation || undefined,
                }));
            if (validations.length > 0 || batchStatus || batchObservation) {
                await collateralApi.registerValidation(id, ids, validations, batchStatus || undefined, batchObservation || undefined);
            }
            const refreshed = await collateralApi.getGuaranteeView(id);
            setData(refreshed);
            toast.success('Validação registrada com sucesso');
            setShowValidationPanel(false);
            setSelectedIds(new Set());
            setEditingIds([]);
            setBatchProcess({});
            setBatchStatus('');
            setBatchObservation('');
        } catch {
            toast.error('Erro ao registrar validação');
        } finally {
            setIsSaving(false);
        }
    };

    return {
        id, navigate, isLoading, data, tabIndex, setTabIndex,
        titles, enabledProcesses, summary,
        selectedIds, setSelectedIds, editingIds,
        showValidationPanel, setShowValidationPanel,
        batchProcess, setBatchProcess, batchStatus, setBatchStatus,
        batchObservation, setBatchObservation, isSaving,
        sortField, setSortField, sortOrder, setSortOrder,
        openBatchPanel, openEditPanel, toggleSelect, handleSaveValidation,
    };
}
